import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import type { AppState } from '../reducer';
import { updateAppState } from '../reducer';

/**
 * One-shot host-supplied setup that runs before the tree mounts; returns a
 * `Partial<AppState>` merged via `updateAppState`.
 *
 * **Must be referentially stable.** `<Bootstrap>`'s effect lists this in
 * its deps; a host that inlines `bootstrapFunc={async () => …}` would
 * re-bootstrap on every render. Define it once at module scope (or wrap in
 * `useCallback` with stable deps) and pass the same reference every time.
 */
export type BootstrapFunc = () => Promise<Partial<AppState>>;

type Props = {
  bootstrapFunc: BootstrapFunc;
  splash?: ReactNode;
  children: ReactNode;
};

export function Bootstrap({ bootstrapFunc, splash = null, children }: Props) {
  const dispatch = useDispatch();
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    bootstrapFunc()
      .then((partial) => {
        if (cancelled) return;
        dispatch(updateAppState(partial));
        setReady(true);
      })
      .catch((err) => {
        if (cancelled) return;

        console.error('[Bootstrap] failed:', err);
        dispatch(updateAppState({ error: String(err) }));
        setReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, [bootstrapFunc, dispatch]);

  if (!ready) return <>{splash}</>;
  return <>{children}</>;
}
